import { useCallback, useState } from 'react';
import { useDebouncedCallback, useLocalStorage } from '@mantine/hooks';

import { RATE_SEARCH_MIN_LENGTH } from './rate-search';

export const RATE_SEARCH_DEBOUNCE_MS = 300;
export const RATE_SEARCH_STORAGE_KEY = 'freightRatesSearchTerm';

type UseRateSearchReturn = {
  searchTerm: string;
  isSearchOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  collapseEmptySearch: () => void;
  handleSearchChange: (value: string) => void;
};

const deserializeSearchTerm = (value: string | undefined): string => {
  if (value === undefined) return '';
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'string' ? parsed : '';
  } catch {
    return '';
  }
};

export const useRateSearch = (): UseRateSearchReturn => {
  const [searchTerm, setSearchTerm] = useLocalStorage<string>({
    key: RATE_SEARCH_STORAGE_KEY,
    defaultValue: '',
    deserialize: deserializeSearchTerm,
  });
  const [isSearchOpen, setIsSearchOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(RATE_SEARCH_STORAGE_KEY);
      return raw !== null && deserializeSearchTerm(raw) !== '';
    } catch {
      return false;
    }
  });

  const applySearchTerm = useDebouncedCallback(setSearchTerm, RATE_SEARCH_DEBOUNCE_MS);

  const openSearch = useCallback(() => setIsSearchOpen(true), []);

  const closeSearch = useCallback(() => {
    applySearchTerm.cancel();
    setIsSearchOpen(false);
    setSearchTerm('');
  }, [applySearchTerm, setSearchTerm]);

  const collapseEmptySearch = useCallback(() => {
    if (searchTerm === '') {
      applySearchTerm.cancel();
      setIsSearchOpen(false);
    }
  }, [searchTerm, applySearchTerm]);

  const handleSearchChange = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length < RATE_SEARCH_MIN_LENGTH) {
        applySearchTerm.cancel();
        setSearchTerm('');
        return;
      }

      applySearchTerm(trimmed);
    },
    [applySearchTerm, setSearchTerm],
  );

  return { searchTerm, isSearchOpen, openSearch, closeSearch, collapseEmptySearch, handleSearchChange };
};
